import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environment/environment';
import { SessionsService } from '../../besharp-sessions/lib/services/sessions.service';
import { buildSessionHeaders } from '../../besharp-sessions/lib/utils/interceptor';

interface ExportPayload {
  columns: string[];
  rows: string[][];
  total_rows: number;
  truncated: boolean;
}

/**
 * Renders the result table of a data query in the DOM.
 * Tables are identified by .data-table[data-datatable-src].
 *
 * The attribute holds the encodeURIComponent'd body of a ```datatable fence, which carries
 * only `{"export_id": "..."}`. The rows themselves are fetched from the API: the agent never
 * has more than a five row preview of a query result, so the table cannot come from the
 * answer text, and putting it there would mean a model writing out rows it cannot see.
 *
 * Rendered collapsed. A data answer should read as prose, with the table one click away
 * rather than several screens of numbers between the user and the next message.
 */
@Injectable({
  providedIn: 'root',
})
export class DataTableService {
  private sessionsService = inject(SessionsService);

  async renderTables(container: HTMLElement | null): Promise<void> {
    if (!container) return;

    const elements = container.querySelectorAll('.data-table[data-datatable-src]');
    if (elements.length === 0) return;

    for (const el of Array.from(elements) as HTMLElement[]) {
      const encoded = el.getAttribute('data-datatable-src') ?? '';
      if (!encoded) continue;

      // Remove immediately so concurrent ngAfterViewChecked calls skip this element.
      el.removeAttribute('data-datatable-src');

      try {
        const { export_id: exportId } = JSON.parse(decodeURIComponent(encoded));
        if (!exportId) throw new Error('datatable block carries no export_id');
        el.innerHTML = this.buildTable(await this.fetchExport(exportId));
        this.bindDownload(el, exportId);
      } catch (error) {
        console.warn('Failed to render data table:', error);
        el.innerHTML = '<span class="datatable-error">Failed to load the data.</span>';
      }
    }
  }

  private async fetchExport(exportId: string): Promise<ExportPayload> {
    // The API authenticates on a Cognito bearer token, not on the session cookie, so the
    // request has to carry the same headers every other call builds.
    const baseUrl = environment.backend.endpoint || '/api';
    const response = await fetch(`${baseUrl}/export/${encodeURIComponent(exportId)}`, {
      headers: new Headers(await buildSessionHeaders(this.sessionsService)),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Export ${exportId} could not be fetched (${response.status})`);
    }
    return (await response.json()) as ExportPayload;
  }

  /**
   * Wire the download button, which the injected HTML cannot carry a listener for.
   *
   * The file is fetched rather than linked: the API wants a bearer token, so a plain
   * <a download> would land on the login error instead of a spreadsheet.
   */
  private bindDownload(container: HTMLElement, exportId: string): void {
    const button = container.querySelector('.datatable-download') as HTMLButtonElement | null;
    if (!button) return;

    button.addEventListener('click', async (event) => {
      // The button lives inside <summary>; without this the click also toggles the section.
      event.preventDefault();
      event.stopPropagation();

      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Preparing...';
      try {
        await this.downloadWorkbook(exportId);
      } catch (error) {
        console.warn('Failed to download the export:', error);
        button.textContent = 'Download failed';
        return;
      } finally {
        button.disabled = false;
      }
      button.textContent = original;
    });
  }

  private async downloadWorkbook(exportId: string): Promise<void> {
    const baseUrl = environment.backend.endpoint || '/api';
    const response = await fetch(`${baseUrl}/export/${encodeURIComponent(exportId)}/xlsx`, {
      headers: new Headers(await buildSessionHeaders(this.sessionsService)),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Export ${exportId} could not be downloaded (${response.status})`);
    }

    const url = URL.createObjectURL(await response.blob());
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = `datahub-export-${exportId}.xlsx`;
      link.click();
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  private buildTable(payload: ExportPayload): string {
    const { columns, rows, total_rows: totalRows, truncated } = payload;

    const head = columns.map((column) => `<th>${escapeHtml(column)}</th>`).join('');
    const body = rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('');

    const label = `${totalRows} ${totalRows === 1 ? 'row' : 'rows'}`;
    // Says "here" rather than just reporting the cap: the download is not the same slice,
    // and a user looking at 2000 of 50000 rows needs to know where the rest is.
    const note = truncated
      ? `<p class="datatable-note">Showing the first ${rows.length} of ${totalRows} rows here — the download has all of them.</p>`
      : '';

    // <details> rather than a component: the table is injected as HTML into markdown output,
    // so the collapse has to work without Angular bindings on these nodes. It reuses
    // .markdown-table so a query result looks like every other table in an answer.
    return `
      <details class="datatable-details">
        <summary class="datatable-summary">
          <span>Show the data (${label})</span>
          <button type="button" class="datatable-download">Download .xlsx</button>
        </summary>
        ${note}
        <div class="datatable-scroll">
          <table class="markdown-table datatable-table">
            <thead><tr>${head}</tr></thead>
            <tbody>${body}</tbody>
          </table>
        </div>
      </details>`;
  }
}

function escapeHtml(value: string): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
